/**
 * The brand itself, drawn: its mark, its name, and the foot every page ends on.
 *
 * These are the components that make a page the TENANT's rather than the fleet's. Every one reads
 * what the brand record says — `logo_url`, `title`, `support_email`, the legal links — and draws
 * nothing for a field the brand left empty: a footer of three dead links is worse than a footer of one
 * live one, and there is no fallback address, because the only other one this fleet knows is the
 * owner's private inbox.
 *
 * There is no app name anywhere in these. A visitor is not in an app; a shopper who can tell they are
 * in a tool is a shopper being shown the wrong thing. That is the one console convention anubis
 * inverts on purpose.
 */
import type { ReactNode } from 'react';

import { MailLink, Link } from './actions';
import { cx, mod } from './cx';
import { Pressable } from './Pressable';

/**
 * The brand's mark, round. `halo` rings it in the accent with a brand-tinted glow — the centre of a
 * link page. Nothing at all without a logo: an empty disc is a broken image in a nicer frame.
 */
export function Mark({
  src,
  alt = '',
  size = 'md',
  halo,
  quiet,
}: {
  src?: string | null | undefined;
  alt?: string | undefined;
  /** `xs` (18px) is a favicon inside a line of text — the app that sent a notification. */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | undefined;
  halo?: boolean | undefined;
  /** Dimmed — the mark repeated at the foot of a page. */
  quiet?: boolean | undefined;
}) {
  if (!src) return null;
  return (
    <img
      src={src}
      alt={alt}
      decoding="async"
      className={cx(
        'a-mark',
        mod('a-mark', 'size', size),
        halo && 'a-mark--halo',
        quiet && 'a-mark--quiet',
      )}
    />
  );
}

/**
 * The mark and the name together, in the display face — the top-left of every page. A link home when
 * given `to` or `href` (the shop's own site, from a checkout or a subscribe page), plain otherwise:
 * "who is this?" should be one press away, but never a dead one.
 */
export function Wordmark({
  logo,
  title,
  size = 'md',
  to,
  href,
  label,
}: {
  logo?: string | null | undefined;
  title?: string | null | undefined;
  size?: 'md' | 'lg' | undefined;
  to?: string | undefined;
  href?: string | undefined;
  label?: string | undefined;
}) {
  const inner = (
    <>
      <Mark src={logo} size={size === 'lg' ? 'lg' : 'md'} />
      {title ? <span className="a-wordmark__title">{title}</span> : null}
    </>
  );
  const className = cx('a-wordmark', mod('a-wordmark', 'size', size));
  if (to !== undefined || href !== undefined) {
    return (
      <Pressable to={to} href={href} label={label} className={className}>
        {inner}
      </Pressable>
    );
  }
  return <span className={className}>{inner}</span>;
}

/**
 * The signed-in person, at the end of a bar — their picture (or their initial), pressed to open
 * `href`: the Settings app, where they manage the account and sign out (2026-10-17, the owner: "like
 * isis does"). Nothing at all without a `user`: a visitor with no session sees no empty disc.
 *
 * Greyscale, always, like the isis nav's avatar: it is the one thing in the chrome that arrives
 * from outside the brand, in any colour, and it must not be the loudest thing on the page. A new
 * tab, as in isis, so the page they were reading is still there when they come back.
 */
export function Account({
  user,
  href,
  label,
}: {
  user?: { name?: string | null; email?: string | null; avatarUrl?: string | null } | null;
  href: string;
  label: string;
}) {
  if (!user) return null;
  const who: string = (user.name || user.email || '').trim();
  return (
    <Pressable href={href} external label={label} className="a-account">
      {user.avatarUrl ? (
        <img src={user.avatarUrl} alt="" decoding="async" className="a-account__picture" />
      ) : (
        <span className="a-account__initial" aria-hidden="true">
          {who.slice(0, 1).toUpperCase() || '·'}
        </span>
      )}
    </Pressable>
  );
}

/**
 * The foot of a page: the brand's legal links, where to write with a question, the mark again, and
 * the copyright line.
 *
 * `band` sets it on the alternate ground behind a hairline, closing a long page (a shop); bare, it is
 * a quiet column under a card. Every part is optional and absent when the brand has not filled it.
 * The mark is repeated on purpose: the last thing on a long landing page, and a page that ends in a
 * hairline and a copyright line ends in nobody's shop.
 */
export function BrandFooter({
  links = [],
  support,
  owner,
  mark,
  band,
  external,
  year = new Date().getFullYear(),
}: {
  links?: ReadonlyArray<{ label: string; href: string | null | undefined }>;
  /** "Questions? Write to" and the brand's `support_email`. */
  support?: { lead: ReactNode; email: string | null | undefined };
  /** The brand's name, for "© 2026 Name". */
  owner?: string | null | undefined;
  mark?: string | null | undefined;
  band?: boolean | undefined;
  /** Open the legal links in a new tab — from a page somebody should not lose. */
  external?: boolean | undefined;
  year?: number | undefined;
}) {
  const present = links.filter((link): link is { label: string; href: string } =>
    Boolean(link.href),
  );
  return (
    <footer className={cx('a-footer', band && 'a-footer--band')}>
      <div className="a-footer__inner">
        <Mark src={mark} size="sm" quiet />
        {present.length > 0 ? (
          <nav
            className="a-footer__links"
            aria-label={present.map((link) => link.label).join(', ')}
          >
            {present.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                external={external}
                tone="inherit"
                underline="hover"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        ) : null}
        {support?.email ? (
          <p className="a-footer__line">
            {support.lead} <MailLink email={support.email} />
          </p>
        ) : null}
        {owner ? (
          <p className="a-footer__line">
            © {year} {owner}
          </p>
        ) : null}
      </div>
    </footer>
  );
}
